import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";

import { apiFetch, ApiError } from "@/lib/api";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";
import { Button } from "@/components/ui/Button";
import { Label } from "@/components/ui/Input";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { cn } from "@/lib/utils";
import {
  BOM_DEFINITION_JSON_OMIT_KEYS,
  BomCompareTestPanel,
  findUnusedInitial,
  JsonTable,
  useBomMe,
} from "./BomShared";

/** Cap read-only definition JsonTable width on wide monitors; scroll horizontally if needed. */
/** Wide but not edge-to-edge: grows with viewport, caps on ultra-wide monitors. */
const BOM_REVIEW_DEFINITION_CARD_BODY =
  "overflow-x-auto p-0";
const BOM_REVIEW_DEFINITION_CARD =
  "mx-auto w-full max-w-[min(96rem,calc(100vw-2.5rem))]";

/** Pending-approval queue: capped for reading, but no horizontal scroll. */
const BOM_PENDING_APPROVALS_CARD_BODY = "mx-auto w-full max-w-6xl p-0";

type Phase = "list" | "review";

function coerceApiError(error: unknown): ApiError {
  return error instanceof ApiError
    ? error
    : new ApiError(0, error instanceof Error ? error.message : String(error));
}

function formatPendingSubmittedAt(value: unknown): string {
  if (value == null || value === "") {
    return "—";
  }
  const raw = String(value);
  const dateOnly = raw.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(dateOnly) ? dateOnly : raw;
}

function parsePositiveIntParam(value: string | null): number | null {
  if (value == null || value.trim() === "") {
    return null;
  }
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function formatCanonicalQty(value: number): string {
  if (!Number.isFinite(value)) {
    return "—";
  }
  return Number.isInteger(value) ? String(value) : String(value);
}

type TemplateRevisionRow = {
  label: string;
  value: number;
  approved_at: string | null;
  description?: string | null;
  submitted_at?: string | null;
};

type RevisionCanonicalStatusPayload = {
  templateId: number;
  revision: number;
  totalCanonicalBoms: number;
  passed: number;
  failed: number;
  items: {
    canonicalBomId: number;
    finishedSku: string;
    displayName: string;
    ok: boolean;
    engineError: string | null;
    mismatchCount: number;
    sampleDiffs: { part: string; expected: number; actual: number }[];
  }[];
};

/** Prefer latest approved other than the proposed row; else latest revision number below proposed; else smallest other revision. */
function pickDefaultCompareRevision(revisions: TemplateRevisionRow[], proposedRevision: number): number | null {
  const others = revisions.filter((row) => Number(row.value) !== proposedRevision);
  if (others.length === 0) {
    return null;
  }
  const approvedNums = others.filter((row) => row.approved_at != null).map((row) => Number(row.value));
  if (approvedNums.length > 0) {
    return Math.max(...approvedNums);
  }
  const lower = others.map((row) => Number(row.value)).filter((value) => value < proposedRevision);
  if (lower.length > 0) {
    return Math.max(...lower);
  }
  return Math.min(...others.map((row) => Number(row.value)));
}

export function ApproveBomRules() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const meQuery = useBomMe();
  const me = meQuery.data;
  const hubWritesAllowed = useHubModuleWrite("bom");
  const bomReadOnlyTitle = "Your role has read-only access to the BoM management hub module.";

  const [phase, setPhase] = useState<Phase>("list");
  const [reviewTemplate, setReviewTemplate] = useState<number | null>(null);
  const [reviewTemplateName, setReviewTemplateName] = useState<string>("");
  const [reviewRevision, setReviewRevision] = useState<number | null>(null);
  const [testSku, setTestSku] = useState("");
  const [compareRevision, setCompareRevision] = useState<number | null>(null);
  const compareBaselineSeededRef = useRef(false);

  const [checkParse, setCheckParse] = useState(false);
  const [checkBom, setCheckBom] = useState(false);
  const [checkUnused, setCheckUnused] = useState(false);
  const [definitionsTab, setDefinitionsTab] = useState<"parse" | "initial" | "rules">("parse");

  const reviewFromUrl = useMemo(() => {
    const templateId = parsePositiveIntParam(searchParams.get("templateId"));
    const revision = parsePositiveIntParam(searchParams.get("revision"));
    return templateId != null && revision != null ? { templateId, revision } : null;
  }, [searchParams]);

  const pendingQuery = useQuery({
    queryKey: ["bom", "pending-approvals"],
    queryFn: () => apiFetch<{ rows: Record<string, unknown>[] }>("/api/bom/pending-approvals"),
  });

  const testSkusQuery = useQuery({
    queryKey: ["bom", "testSkus", reviewTemplate],
    queryFn: () =>
      apiFetch<{ skus: { value: string; label: string }[] }>("/api/bom/test-skus", {
        query: { templateId: reviewTemplate as number },
      }),
    enabled: reviewTemplate != null,
  });

  const initialQuery = useQuery({
    queryKey: ["bom", "rows", "initial", reviewTemplate, reviewRevision],
    queryFn: () =>
      apiFetch<{ rows: Record<string, unknown>[] }>("/api/bom/rows/initial", {
        query: { templateId: reviewTemplate as number, revision: reviewRevision as number },
      }),
    enabled: reviewTemplate != null && reviewRevision != null && phase === "review",
  });

  const templateRevisionsQuery = useQuery({
    queryKey: ["bom", "template-revisions", reviewTemplate],
    queryFn: () =>
      apiFetch<{ revisions: TemplateRevisionRow[] }>("/api/bom/template-revisions", {
        query: { templateId: reviewTemplate as number },
      }),
    enabled: reviewTemplate != null && phase === "review",
  });

  const compareInitialQuery = useQuery({
    queryKey: ["bom", "rows", "initial", "compare", reviewTemplate, compareRevision],
    queryFn: () =>
      apiFetch<{ rows: Record<string, unknown>[] }>("/api/bom/rows/initial", {
        query: { templateId: reviewTemplate as number, revision: compareRevision as number },
      }),
    enabled: reviewTemplate != null && compareRevision != null && phase === "review",
  });

  useEffect(() => {
    if (phase !== "review") {
      compareBaselineSeededRef.current = false;
      return;
    }
    if (!templateRevisionsQuery.data || reviewRevision == null) {
      return;
    }
    if (compareBaselineSeededRef.current) {
      return;
    }
    if (compareRevision != null) {
      compareBaselineSeededRef.current = true;
      return;
    }
    compareBaselineSeededRef.current = true;
    setCompareRevision(pickDefaultCompareRevision(templateRevisionsQuery.data.revisions, reviewRevision));
  }, [phase, reviewRevision, templateRevisionsQuery.data, compareRevision]);

  const compareRevisionOptions = useMemo(() => {
    const rows = templateRevisionsQuery.data?.revisions ?? [];
    return rows
      .filter((row) => Number(row.value) !== reviewRevision)
      .map((row) => ({ value: Number(row.value), label: String(row.label) }));
  }, [templateRevisionsQuery.data?.revisions, reviewRevision]);

  const revisionNotes = useMemo(() => {
    if (reviewRevision == null) {
      return null;
    }
    const rows: TemplateRevisionRow[] = templateRevisionsQuery.data?.revisions ?? [];
    const row = rows.find((item) => Number(item.value) === reviewRevision);
    const raw = row?.description;
    if (raw == null || typeof raw !== "string") {
      return null;
    }
    const trimmed = raw.trim();
    return trimmed.length > 0 ? trimmed : null;
  }, [templateRevisionsQuery.data?.revisions, reviewRevision]);

  const parseDefinitionQuery = useQuery({
    queryKey: ["bom", "rows", "skuParse", reviewTemplate, reviewRevision],
    queryFn: () =>
      apiFetch<{ rows: Record<string, unknown>[] }>("/api/bom/rows/sku-parse", {
        query: { templateId: reviewTemplate as number, revision: reviewRevision as number },
      }),
    enabled: reviewTemplate != null && reviewRevision != null && phase === "review",
  });

  const rulesDefinitionQuery = useQuery({
    queryKey: ["bom", "rows", "rules", reviewTemplate, reviewRevision],
    queryFn: () =>
      apiFetch<{ rows: Record<string, unknown>[] }>("/api/bom/rows/rules", {
        query: { templateId: reviewTemplate as number, revision: reviewRevision as number },
      }),
    enabled: reviewTemplate != null && reviewRevision != null && phase === "review",
  });

  const canonicalStatusQuery = useQuery({
    queryKey: ["bom", "revision-canonical-status", reviewTemplate, reviewRevision],
    queryFn: () =>
      apiFetch<RevisionCanonicalStatusPayload>("/api/bom/revision-canonical-status", {
        query: { templateId: reviewTemplate as number, revision: reviewRevision as number },
      }),
    enabled: reviewTemplate != null && reviewRevision != null && phase === "review",
    staleTime: 15_000,
  });

  const testMutation = useMutation({
    mutationFn: async () => {
      if (reviewTemplate == null || reviewRevision == null || !testSku.trim()) {
        throw new Error("Select a test SKU.");
      }
      const templateId = reviewTemplate;
      const proposedRev = reviewRevision;
      const sku = testSku.trim();

      type Side = {
        parse?: { rows: unknown[] };
        spec?: { rows: unknown[] };
        parseErr?: ApiError;
        specErr?: ApiError;
      };
      const baseline: Side = {};
      const proposed: Side = {};

      if (compareRevision != null) {
        const baselineRev = compareRevision;
        try {
          baseline.parse = await apiFetch<{ rows: unknown[] }>("/api/bom/preview/parse-sku-specific", {
            query: { sku, templateId, revision: baselineRev, useCache: "true" },
          });
        } catch (error: unknown) {
          baseline.parseErr = coerceApiError(error);
        }
        try {
          baseline.spec = await apiFetch<{ rows: unknown[] }>("/api/bom/preview/bom-specific", {
            query: { sku, templateId, revision: baselineRev },
          });
        } catch (error: unknown) {
          baseline.specErr = coerceApiError(error);
        }
      }

      try {
        proposed.parse = await apiFetch<{ rows: unknown[] }>("/api/bom/preview/parse-sku-specific", {
          query: { sku, templateId, revision: proposedRev, useCache: "true" },
        });
      } catch (error: unknown) {
        proposed.parseErr = coerceApiError(error);
      }
      try {
        proposed.spec = await apiFetch<{ rows: unknown[] }>("/api/bom/preview/bom-specific", {
          query: { sku, templateId, revision: proposedRev },
        });
      } catch (error: unknown) {
        proposed.specErr = coerceApiError(error);
      }

      return { baseline, proposed };
    },
  });

  const { mutate: runCompareTestMutate, reset: resetTestMutation } = testMutation;

  useEffect(() => {
    if (reviewFromUrl == null) {
      if (phase === "list" && reviewTemplate == null && reviewRevision == null) {
        return;
      }
      setPhase("list");
      setReviewTemplate(null);
      setReviewTemplateName("");
      setReviewRevision(null);
      setCompareRevision(null);
      compareBaselineSeededRef.current = false;
      resetTestMutation();
      return;
    }

    const nameFromPending =
      pendingQuery.data?.rows.find(
        (row) =>
          Number(row.template_id) === reviewFromUrl.templateId &&
          Number(row.revision) === reviewFromUrl.revision,
      )?.name ?? "";
    const nameText = String(nameFromPending);
    const alreadyReviewing =
      phase === "review" &&
      reviewTemplate === reviewFromUrl.templateId &&
      reviewRevision === reviewFromUrl.revision;

    if (!alreadyReviewing) {
      setPhase("review");
      setReviewTemplate(reviewFromUrl.templateId);
      setReviewTemplateName(nameText);
      setReviewRevision(reviewFromUrl.revision);
      setTestSku("");
      setDefinitionsTab("parse");
      setCompareRevision(null);
      compareBaselineSeededRef.current = false;
      setCheckParse(false);
      setCheckBom(false);
      setCheckUnused(false);
      resetTestMutation();
      return;
    }

    if (nameText && reviewTemplateName !== nameText) {
      setReviewTemplateName(nameText);
    }
  }, [
    phase,
    pendingQuery.data?.rows,
    resetTestMutation,
    reviewFromUrl,
    reviewRevision,
    reviewTemplate,
    reviewTemplateName,
  ]);

  useEffect(() => {
    if (phase !== "review") {
      return;
    }
    if (reviewTemplate == null || reviewRevision == null) {
      return;
    }
    const sku = testSku.trim();
    if (!sku) {
      return;
    }
    runCompareTestMutate();
  }, [phase, reviewTemplate, reviewRevision, compareRevision, testSku, runCompareTestMutate]);

  const unusedCount = findUnusedInitial(
    initialQuery.data?.rows,
    testMutation.data?.proposed?.spec?.rows as Record<string, unknown>[] | undefined,
  ).length;

  const running = testMutation.isPending;
  const baselineParseState = {
    data: testMutation.data?.baseline?.parse,
    error: testMutation.data?.baseline?.parseErr ?? null,
    isFetching: running,
  };
  const baselineSpecificState = {
    data: testMutation.data?.baseline?.spec,
    error: testMutation.data?.baseline?.specErr ?? null,
    isFetching: running,
  };
  const proposedParseState = {
    data: testMutation.data?.proposed?.parse,
    error: testMutation.data?.proposed?.parseErr ?? null,
    isFetching: running,
  };
  const proposedSpecificState = {
    data: testMutation.data?.proposed?.spec,
    error: testMutation.data?.proposed?.specErr ?? null,
    isFetching: running,
  };

  const approveMutation = useMutation({
    mutationFn: () => {
      if (reviewTemplate == null || reviewRevision == null) {
        throw new Error("Missing selection.");
      }
      return apiFetch("/api/bom/approve-revision", {
        method: "POST",
        body: { templateId: reviewTemplate, revision: reviewRevision },
      });
    },
    onSuccess: () => {
      const approvedTemplateId = reviewTemplate;
      setSearchParams({}, { replace: true });
      setPhase("list");
      setReviewTemplate(null);
      setReviewTemplateName("");
      setReviewRevision(null);
      setTestSku("");
      setCompareRevision(null);
      compareBaselineSeededRef.current = false;
      setCheckParse(false);
      setCheckBom(false);
      setCheckUnused(false);
      void queryClient.invalidateQueries({ queryKey: ["bom", "pending-approvals"] });
      if (approvedTemplateId != null) {
        void queryClient.invalidateQueries({ queryKey: ["bom", "revisions", approvedTemplateId] });
        void queryClient.invalidateQueries({ queryKey: ["bom", "template-latest-approved", approvedTemplateId] });
      }
    },
  });

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-xl font-semibold text-slate-900">Approve BoM rules</h1>
        <p className="text-sm text-slate-600">
          Revisions that are submitted and not yet approved appear below. Open one to run the same tests as the author,
          then confirm each checklist item and approve.
        </p>
      </header>

      {meQuery.isSuccess && !me?.staff ? (
        <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          No <code className="text-xs">sgs.staff</code> row for your account. You will not be able to approve until your
          staff id resolves from your email.
        </p>
      ) : null}
      {meQuery.isError ? (
        <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
          Could not load your BoM staff profile ({meQuery.error.message}). Reload the page or sign out and sign in again.
        </p>
      ) : null}

      {phase === "list" ? (
        <Card className="mx-auto w-full max-w-6xl">
          <CardHeader>
            <CardTitle>Waiting for approval</CardTitle>
          </CardHeader>
          <CardBody className={BOM_PENDING_APPROVALS_CARD_BODY}>
            {pendingQuery.isLoading ? <p className="p-3 text-sm text-slate-500">Loading…</p> : null}
            {pendingQuery.isError ? (
              <p className="p-3 text-sm text-rose-800">
                Could not load the approval queue ({pendingQuery.error.message}).
              </p>
            ) : null}
            {pendingQuery.data?.rows.length === 0 ? (
              <p className="p-4 text-sm text-slate-500">Nothing in the queue.</p>
            ) : (
              <div className="divide-y divide-slate-100">
                <div className="grid grid-cols-[minmax(10rem,1.2fr)_minmax(0,2fr)_minmax(11rem,0.9fr)_auto] gap-3 bg-slate-50 px-4 py-2 text-left text-xs uppercase text-slate-500">
                  <span>Template</span>
                  <span>Description</span>
                  <span>Submitted</span>
                  <span className="text-right">Action</span>
                </div>
                {(pendingQuery.data?.rows ?? []).map((row) => {
                  const templateId = Number(row.template_id);
                  const rev = Number(row.revision);
                  return (
                    <div
                      key={`${templateId}-${rev}`}
                      className="grid grid-cols-[minmax(10rem,1.2fr)_minmax(0,2fr)_minmax(11rem,0.9fr)_auto] items-start gap-3 px-4 py-3 text-sm"
                    >
                      <div className="min-w-0">
                        <p className="break-words font-medium text-slate-900">{String(row.name ?? "")}</p>
                        <p className="mt-1 font-mono text-xs text-slate-500">Revision {String(row.revision ?? "")}</p>
                      </div>
                      <p className="min-w-0 break-words text-xs leading-relaxed text-slate-600">
                        {String(row.revision_description ?? "—")}
                      </p>
                      <div className="min-w-0 text-xs text-slate-600">
                        <p className="break-words text-slate-800">{String(row.submitted_by ?? "—")}</p>
                        <p className="mt-1 font-mono text-slate-500">{formatPendingSubmittedAt(row.submitted_at)}</p>
                      </div>
                      <div className="text-right">
                        <Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          onClick={() => {
                            setSearchParams({ templateId: String(templateId), revision: String(rev) });
                            setReviewTemplate(templateId);
                            setReviewTemplateName(String(row.name ?? ""));
                            setReviewRevision(rev);
                            setTestSku("");
                            setDefinitionsTab("parse");
                            setCompareRevision(null);
                            compareBaselineSeededRef.current = false;
                            setCheckParse(false);
                            setCheckBom(false);
                            setCheckUnused(false);
                            testMutation.reset();
                            setPhase("review");
                          }}
                        >
                          Review
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardBody>
        </Card>
      ) : null}

      {phase === "review" && reviewTemplate != null && reviewRevision != null ? (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-lg font-medium text-slate-900">
              Review template {reviewTemplateName.trim() || "—"}, revision {reviewRevision}
            </h2>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => {
                setSearchParams({}, { replace: true });
                setPhase("list");
                setReviewTemplate(null);
                setReviewTemplateName("");
                setReviewRevision(null);
                setCompareRevision(null);
                compareBaselineSeededRef.current = false;
                testMutation.reset();
              }}
            >
              Back to list
            </Button>
          </div>

          {revisionNotes ? (
            <div className="rounded-md border border-slate-200 bg-slate-50/90 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Revision notes</p>
              <p className="mt-2 whitespace-pre-wrap text-sm text-slate-900">{revisionNotes}</p>
            </div>
          ) : null}

          {canonicalStatusQuery.isLoading ? (
            <p className="text-sm text-slate-500">Loading canonical BOM status…</p>
          ) : canonicalStatusQuery.isError ? (
            <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
              Could not load canonical BOM status:{" "}
              {canonicalStatusQuery.error instanceof Error ? canonicalStatusQuery.error.message : "unknown error"}
            </p>
          ) : canonicalStatusQuery.data != null && canonicalStatusQuery.data.totalCanonicalBoms === 0 ? (
            <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
              No canonical BOM references for this template. When{" "}
              <code className="rounded bg-slate-200/80 px-1 text-xs">bom.canonical_bom</code> rows point at this template,
              this report shows how many gold-standard SKUs match this revision&apos;s{" "}
              <code className="rounded bg-slate-200/80 px-1 text-xs">create_bom_specific</code> output.
            </div>
          ) : canonicalStatusQuery.data != null ? (
            <div
              className={cn(
                "rounded-md border px-4 py-3",
                canonicalStatusQuery.data.failed === 0
                  ? "border-emerald-200 bg-emerald-50/90"
                  : "border-amber-200 bg-amber-50/90",
              )}
            >
              <p className="text-sm font-medium text-slate-900">Canonical BOM status</p>
              <p className="mt-1 text-sm text-slate-800">
                This revision matches{" "}
                <span className="font-mono font-semibold">{canonicalStatusQuery.data.passed}</span>
                {" of "}
                <span className="font-mono font-semibold">{canonicalStatusQuery.data.totalCanonicalBoms}</span>
                {" canonical BOM"}
                {canonicalStatusQuery.data.totalCanonicalBoms === 1 ? "" : "s"} for this template (exploded quantities
                vs <code className="rounded bg-white/80 px-1 text-xs">bom.canonical_bom_line</code>).
              </p>
              {canonicalStatusQuery.data.failed > 0 ? (
                <details className="mt-3 rounded-md border border-amber-100 bg-white/80">
                  <summary className="cursor-pointer px-3 py-2 text-sm font-medium text-amber-950">
                    {canonicalStatusQuery.data.failed} mismatch
                    {canonicalStatusQuery.data.failed === 1 ? "" : "es"} — details
                  </summary>
                  <ul className="space-y-3 border-t border-amber-100 p-3 text-sm">
                    {canonicalStatusQuery.data.items
                      .filter((item) => !item.ok)
                      .map((item) => (
                        <li key={item.canonicalBomId} className="rounded border border-slate-100 bg-slate-50/90 p-2">
                          <p className="font-mono text-xs font-medium text-slate-900">{item.finishedSku}</p>
                          <p className="text-xs text-slate-600">{item.displayName}</p>
                          {item.engineError ? (
                            <p className="mt-2 text-xs text-rose-700">{item.engineError}</p>
                          ) : null}
                          {item.sampleDiffs.length > 0 ? (
                            <table className="mt-2 w-full min-w-0 text-xs">
                              <thead>
                                <tr className="text-left text-slate-500">
                                  <th className="py-1 pr-2 font-medium">Part</th>
                                  <th className="py-1 pr-2 font-medium">Canonical qty</th>
                                  <th className="py-1 font-medium">Engine qty</th>
                                </tr>
                              </thead>
                              <tbody>
                                {item.sampleDiffs.map((row) => (
                                  <tr key={row.part} className="border-t border-slate-200">
                                    <td className="py-1 pr-2 font-mono">{row.part}</td>
                                    <td className="py-1 pr-2 font-mono">{formatCanonicalQty(row.expected)}</td>
                                    <td className="py-1 font-mono">{formatCanonicalQty(row.actual)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          ) : null}
                          {item.mismatchCount > item.sampleDiffs.length ? (
                            <p className="mt-1 text-xs text-slate-500">
                              Showing {item.sampleDiffs.length} of {item.mismatchCount} differing parts.
                            </p>
                          ) : null}
                        </li>
                      ))}
                  </ul>
                </details>
              ) : null}
            </div>
          ) : null}

          <details className="rounded-lg border border-slate-200 bg-white shadow-sm">
            <summary className="cursor-pointer px-4 py-3 text-sm font-medium text-slate-800 hover:bg-slate-50">
              Template definitions for this revision (parse rules, initial BoM, engine rules)
            </summary>
            <div className="border-t border-slate-100 p-4">
              <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-3">
                <button
                  type="button"
                  className={cn(
                    "rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                    definitionsTab === "parse"
                      ? "bg-slate-900 text-white"
                      : "bg-slate-100 text-slate-700 hover:bg-slate-200",
                  )}
                  onClick={() => setDefinitionsTab("parse")}
                >
                  Parse rules
                </button>
                <button
                  type="button"
                  className={cn(
                    "rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                    definitionsTab === "initial"
                      ? "bg-slate-900 text-white"
                      : "bg-slate-100 text-slate-700 hover:bg-slate-200",
                  )}
                  onClick={() => setDefinitionsTab("initial")}
                >
                  Initial BoM
                </button>
                <button
                  type="button"
                  className={cn(
                    "rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                    definitionsTab === "rules"
                      ? "bg-slate-900 text-white"
                      : "bg-slate-100 text-slate-700 hover:bg-slate-200",
                  )}
                  onClick={() => setDefinitionsTab("rules")}
                >
                  Engine rules
                </button>
              </div>
              <div className="pt-4">
                {definitionsTab === "parse" ? (
                  <Card className={BOM_REVIEW_DEFINITION_CARD}>
                    <CardHeader>
                      <CardTitle>Parse rule definitions</CardTitle>
                    </CardHeader>
                    <CardBody className={BOM_REVIEW_DEFINITION_CARD_BODY}>
                      {parseDefinitionQuery.isLoading ? (
                        <p className="p-4 text-sm text-slate-500">Loading parse rules…</p>
                      ) : parseDefinitionQuery.isError ? (
                        <p className="p-4 text-sm text-rose-700">{(parseDefinitionQuery.error as Error).message}</p>
                      ) : (
                        <JsonTable
                          rows={parseDefinitionQuery.data?.rows}
                          comfortable
                          omitKeys={BOM_DEFINITION_JSON_OMIT_KEYS}
                        />
                      )}
                    </CardBody>
                  </Card>
                ) : null}
                {definitionsTab === "initial" ? (
                  <Card className={BOM_REVIEW_DEFINITION_CARD}>
                    <CardHeader>
                      <CardTitle>Initial BoM (template rows)</CardTitle>
                    </CardHeader>
                    <CardBody className={BOM_REVIEW_DEFINITION_CARD_BODY}>
                      {initialQuery.isLoading ? (
                        <p className="p-4 text-sm text-slate-500">Loading initial BoM…</p>
                      ) : initialQuery.isError ? (
                        <p className="p-4 text-sm text-rose-700">{(initialQuery.error as Error).message}</p>
                      ) : (
                        <JsonTable rows={initialQuery.data?.rows} comfortable omitKeys={BOM_DEFINITION_JSON_OMIT_KEYS} />
                      )}
                    </CardBody>
                  </Card>
                ) : null}
                {definitionsTab === "rules" ? (
                  <Card className={BOM_REVIEW_DEFINITION_CARD}>
                    <CardHeader>
                      <CardTitle>BoM engine rules</CardTitle>
                    </CardHeader>
                    <CardBody className={BOM_REVIEW_DEFINITION_CARD_BODY}>
                      {rulesDefinitionQuery.isLoading ? (
                        <p className="p-4 text-sm text-slate-500">Loading rules…</p>
                      ) : rulesDefinitionQuery.isError ? (
                        <p className="p-4 text-sm text-rose-700">{(rulesDefinitionQuery.error as Error).message}</p>
                      ) : (
                        <JsonTable
                          rows={rulesDefinitionQuery.data?.rows}
                          comfortable
                          omitKeys={BOM_DEFINITION_JSON_OMIT_KEYS}
                        />
                      )}
                    </CardBody>
                  </Card>
                ) : null}
              </div>
            </div>
          </details>

          <BomCompareTestPanel
            testSku={testSku}
            onTestSku={setTestSku}
            compareRevision={compareRevision}
            onCompareRevision={setCompareRevision}
            compareRevisionOptions={compareRevisionOptions}
            skuOptions={testSkusQuery.data?.skus ?? []}
            baselineLabel={
              compareRevision != null
                ? `Revision ${compareRevision} — baseline (compare against)`
                : "Baseline (no prior revision)"
            }
            hasBaselineRevision={compareRevisionOptions.length > 0}
            proposedLabel={
              reviewRevision != null
                ? `Revision ${reviewRevision} — submitted for approval`
                : "Revision under review"
            }
            baselineParse={baselineParseState}
            baselineSpecific={baselineSpecificState}
            baselineInitialRows={compareInitialQuery.data?.rows}
            proposedParse={proposedParseState}
            proposedSpecific={proposedSpecificState}
            proposedInitialRows={initialQuery.data?.rows}
            density="comfortable"
            runTestBlocked={!hubWritesAllowed}
            runTestBlockedTitle={bomReadOnlyTitle}
          />
          {testMutation.isError ? (
            <p className="text-sm text-rose-700">{(testMutation.error as Error).message}</p>
          ) : null}

          <div className="space-y-2 rounded-md border border-slate-200 bg-white p-4">
            <p className="text-sm font-medium text-slate-800">Approvals (required)</p>
            <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={bomReadOnlyTitle} layout="block">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-300"
                  checked={checkParse}
                  disabled={!hubWritesAllowed}
                  onChange={(event) => setCheckParse(event.target.checked)}
                />
                {
                  "I have reviewed this revision's parse rules (including ignore for configuration) and the parse output below for a representative test SKU"
                }
              </label>
            </ReadOnlyHoverLayer>
            <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={bomReadOnlyTitle} layout="block">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-300"
                  checked={checkBom}
                  disabled={!hubWritesAllowed}
                  onChange={(event) => setCheckBom(event.target.checked)}
                />
                I have reviewed the exploded BoM (create_bom_specific) for that SKU
              </label>
            </ReadOnlyHoverLayer>
            <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={bomReadOnlyTitle} layout="block">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-300"
                  checked={checkUnused}
                  disabled={!hubWritesAllowed}
                  onChange={(event) => setCheckUnused(event.target.checked)}
                />
                I confirm there are no unexpected initial-BoM variables (unused list{unusedCount > 0 ? ` has ${unusedCount} item(s)` : " is clear"})
              </label>
            </ReadOnlyHoverLayer>
            <div className="pt-2">
              <Label>Recorded approver (sgs.staff)</Label>
              <p className="mt-1 text-sm text-slate-900">
                {me?.staff ? (
                  <>
                    {me.staff.fullName ?? "—"}
                    {me.staff.email ? (
                      <span className="text-slate-600">{` (${me.staff.email})`}</span>
                    ) : null}
                  </>
                ) : (
                  <span className="text-slate-500">No staff row for this session.</span>
                )}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={bomReadOnlyTitle}>
              <Button
                type="button"
                onClick={() => {
                  if (!hubWritesAllowed) return;
                  approveMutation.mutate();
                }}
                disabled={
                  !hubWritesAllowed ||
                  !checkParse ||
                  !checkBom ||
                  !checkUnused ||
                  !me?.staff?.id ||
                  approveMutation.isPending
                }
              >
                {approveMutation.isPending ? "Approving…" : "Approve revision"}
              </Button>
            </ReadOnlyHoverLayer>
            {approveMutation.isError ? (
              <span className="text-sm text-rose-700">{(approveMutation.error as Error).message}</span>
            ) : null}
          </div>
        </div>
      ) : null}

    </div>
  );
}
